import {
  Component, EventEmitter, Input, Output, QueryList,
  ViewChildren,
} from '@angular/core';
import {TreeNode} from '../../../../content/shared/interfaces/tree-node.interface';
import {RadioTreeNodeComponent} from '../tree-node/radio-tree-node/radio-tree-node.component';

/**
 * Class for the TreeListComponent
 * @class TreeListComponent
 *
 * @description Custom Tree list component utilizing radio inputs for single selection or checkbox inputs for multi select
 *
 * @usage
 *
 * ```html
 * <advtech-tree-list
 *    [data]="choices"
 *    [multiple]="multiple"
 *    [value]="value"
 *    (onChange)="hTreeSelection($event)"
 * ></advtech-tree-list>
 * ```
 *
 * # Inputs
 * |name|type|description|
 * |---|---|---|
 * |data|[[TreeNode]][]|List of tree nodes to display|
 * |multiple|boolean|Defaults to false and uses radio inputs. True will use checkbox inputs|
 * |value|[[TreeNode]][]|The current input value. Used to select the element in the modal on load|
 *
 * # Outputs
 * |name|type|description|
 * |---|---|---|
 * |onChange|[EventEmitter]{@link https://angular.io/api/core/EventEmitter}<{value: [[TreeNode]][]}>|Emits the value on tree node selection|
 */
@Component({
  selector: 'advtech-tree-list',
  templateUrl: './tree-list.component.html',
})
export class TreeListComponent {
  /**
   * Data to be displayed
   * @type {TreeNode[]}
   */
  @Input()
  data: TreeNode[];

  /**
   * Should use radio or checkbox inputs
   * @type {boolean}
   */
  @Input()
  multiple: boolean = false;

  /**
   * Current value of the input. Used to display current selection in the modal
   * @type {Array}
   */
  @Input()
  value: TreeNode[] = [];

  /**
   * Emits the value on change
   * @type {EventEmitter<any>}
   */
  @Output()
  onTreeListValueChange: EventEmitter<{ value: TreeNode[] }> = new EventEmitter();

  @ViewChildren(RadioTreeNodeComponent)
  radioTreeNodes: QueryList<RadioTreeNodeComponent>;

  /**
   * Handles the tree node selection and emits the value
   * @param {{value: TreeNode}} e The selected tree node
   * @returns {void}
   */
  hTreeNodeSelection(e: { value: TreeNode }): void {
    let ret = Array.prototype.concat([], this.value);
    e.value.checked && ret.push(e.value);
    if (this.multiple) {
      ret = ret.filter((elm) => {
        if (elm.value !== e.value.value) {
          return elm;
        }
        if (elm.value === e.value.value && e.value.checked) {
          return elm;
        }
      });

    }
    else {
      ret = e.value.checked ? [e.value] : [];
      this.updateRadioTreeNodes(ret);
    }
    this.onTreeListValueChange.emit({value: ret});
    this.value = ret;
  }

  private updateRadioTreeNodes(ret: any[]) {
    this.radioTreeNodes.length && this.radioTreeNodes.forEach((elm) => {
      if (ret && ret.length > 0) {
        elm.isChecked = elm.node.value === ret[0].value;
        elm.setCheckedForChildren(ret);
      }
      else {
        elm.isChecked = false;
        elm.setCheckedForChildren(ret);
      }
    });
  }
}


// WEBPACK FOOTER //
// ./src/app/shared/components/tree/tree-list/tree-list.component.ts